<template>
  <div>
    <!-- Breadcrumb -->
    <app-breadcrumb>
      <template #contentright>
        <span />
      </template>
    </app-breadcrumb>

    <!-- Filters -->
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5>
          {{ $t('Filters') }}
        </h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col
            v-if="$can('log.list_all_auth_log', 'Log')"
            cols
            class="mb-md-0 mb-2"
          >
            <label>Người dùng</label>
            <v-select
              v-model="searchUserId"
              label="name"
              :filterable="false"
              :options="userOptions"
              @search="fetchUsers"
            >
              <template slot="no-options">
                tìm user...
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <div class="d-center">
                  {{ option.username }} ({{ option.full_name }})
                </div>
              </template>
              <template
                slot="selected-option"
                slot-scope="option"
              >
                <div class="selected d-center">
                  {{ option.username }} ({{ option.full_name }})
                </div>
              </template>
            </v-select>

          </b-col>
          <b-col
            cols
            class="mb-md-0 mb-2"
          >
            <label>Loại</label>
            <b-form-select
              v-model="searchType"
              @change="fetchLog"
            >

              <b-form-select-option
                v-for="option in typeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.text }}
              </b-form-select-option>
            </b-form-select>

          </b-col>
          <b-col
            cols
            class="mb-md-0 mb-2"
          >
            <label>Hình thức đăng nhập</label>
            <b-form-select
              v-model="searchProvider"
              @change="fetchLog"
            >
              <b-form-select-option
                v-for="option in providerOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.text }}
              </b-form-select-option>
            </b-form-select>

          </b-col>
          <b-col
            cols
            class="mb-md-0 mb-2"
          >
            <label>Từ ngày</label>
            <b-input-group class="input-group-merge">
              <flat-pickr
                ref="startDateRef"
                v-model="startDate"
                class="form-control"
                :config="configDate"
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="!startDate ? 'CalendarIcon' : 'XIcon'"
                  @click.prevent="!startDate ? $refs.startDateRef.$el.focus() : startDate = ''"
                />
              </b-input-group-append>
            </b-input-group>
          </b-col>

          <b-col
            cols
            class="mb-md-0 mb-2"
          >
            <label>Đến ngày</label>
            <b-input-group class="input-group-merge">
              <flat-pickr
                ref="endDateRef"
                v-model="endDate"
                class="form-control"
                :config="configDate"
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="!endDate ? 'CalendarIcon' : 'XIcon'"
                  @click.prevent="!endDate ? $refs.endDateRef.$el.focus() : endDate = ''"
                />
              </b-input-group-append>
            </b-input-group>
          </b-col>

          <b-col
            cols
            class="mb-md-0 mb-2"
          >
            <label>Tìm kiếm</label>
            <b-input-group>
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block"
                placeholder="email,số điện thoại, ip"
                @keyup.enter="fetchLog"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  @click="fetchLog"
                >
                  <feather-icon icon="SearchIcon" />
                </b-button>
              </b-input-group-append>
            </b-input-group>

          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Table Container Card -->
    <b-overlay
      :show="loading"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      rounded="sm"
    >
      <b-card
        no-body
        class="mb-0"
      >

        <div class="m-2">
          <!-- Table Top -->
          <b-row>
            <!-- Per Page -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label>{{ $t('Show') }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
              <label>{{ $t('entries') }}</label>
            </b-col>

            <!-- Actions -->
            <b-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-items-center justify-content-end">
                <b-button
                  variant="outline-primary"
                  @click="authLogForceLogoutExpired"
                >
                  <span class="text-nowrap">Đăng xuất các phiên đã hết hạn</span>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>

        <b-table
          ref="refListTable"
          class="position-relative"
          :items="itemsData"
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          responsive
          show-empty
          empty-text="Không tìm thấy kết quả"
          :sort-desc.sync="isSortDirDesc"
        >

          <!-- Column: user_id -->
          <template #cell(user)="data">
            <b-link
              :to="{ name: 'users-view', params: { id: data.item.user.id } }"
              class="font-weight-bold d-block text-nowrap"
            >
              {{ data.item.user.full_name }}
            </b-link>
            <small class="text-muted">@{{ data.item.user.username }}</small>
          </template>

          <!-- type -->
          <template #cell(type)="data">
            <b-badge
              pill
              :variant="`light-${resolveTypeVariant(data.item.type)}`"
              class="text-capitalize"
            >
              {{ resolveTypeText(data.item.type) }}
            </b-badge>
          </template>

          <!-- provider -->
          <template #cell(provider)="data">
            {{ resolveProviderText(data.item.provider) }}
          </template>

          <!-- device -->
          <template #cell(device)="data">
            {{ deviceType(data.item.ua) }}
          </template>

          <!-- browser -->
          <template #cell(browser)="data">
            {{ getBrowser(data.item.ua) }}
          </template>

          <!-- action -->
          <template #cell(actions)="data">
            <b-button
              v-ripple.400="'rgba(40, 199, 111, 0.15)'"
              v-b-tooltip
              size="sm"
              class="btn-icon ml-1"
              variant="outline-primary"
              title="Thu hồi quyền truy cập"
              @click="confirmForceLogout(data.item)"
            >
              <feather-icon icon="LogOutIcon" />
            </b-button>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">{{ $t('show_to_entries', { from: dataMeta.from, to: dataMeta.to, of: dataMeta.of }) }}</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >

              <b-pagination
                v-model="currentPage"
                :total-rows="totalEntries"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BOverlay, BCard, BCardHeader, BCardBody, BRow, BCol, BFormInput, BButton, BTable, BInputGroup, BInputGroupAppend,
  BBadge, BPagination, BFormSelect, BFormSelectOption, BLink, VBTooltip,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import { ref, computed, watch } from '@vue/composition-api'
// import { showToast } from '@/@core/utils/utils'
import AppBreadcrumb from '@core/layouts/components/AppBreadcrumb.vue'
import store from '@/store'
import Ripple from 'vue-ripple-directive'
import useFileManager from '../files/useFileManager';

export default {
  components: {
    AppBreadcrumb,
    BOverlay,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BLink,
    BFormInput,
    BInputGroup,
    BFormSelect,
    BFormSelectOption,
    BInputGroupAppend,
    BPagination,
    flatPickr,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  setup(props, context) {
    // Table Handlers
    const tableColumns = [
      { key: 'user', sortable: false, thAttr: { width: '250' } },
      {
        key: 'type', sortable: false, label: 'Loại', thAttr: { width: '100' },
      },
      { key: 'provider', sortable: false, label: 'Hình thức đăng nhập' },
      {
        key: 'logdate', sortable: false, label: 'Ngày truy cập', thAttr: { width: '150' },
      },
      {
        key: 'ip', sortable: false, label: 'Ip truy cập', thAttr: { width: '150' },
      },
      {
        key: 'device', sortable: false, label: 'Thiết bị', thAttr: { width: '150' },
      },
      {
        key: 'browser', sortable: false, label: 'Trình duyệt', thAttr: { width: '150' },
      },
      {
        key: 'actions', sortable: false, label: 'Hành động', thAttr: { width: '120', class: 'text-center' },
      },
    ];
    const loading = ref(false);
    const perPage = ref(10);
    const totalEntries = ref(0);
    const currentPage = ref(1);
    const perPageOptions = [10, 25, 50, 100];
    const searchUserId = ref(0);
    const searchQuery = ref('');
    const searchType = ref('');
    const searchProvider = ref('');
    const sortBy = ref('id');
    const isSortDirDesc = ref(true);
    const isPaginate = ref(1);
    const itemsData = ref([]);
    const startDate = ref(null);
    const endDate = ref(null);

    const refListTable = ref(null);
    const userOptions = ref([]);
    const typeOptions = [
      { value: '', text: 'Tất cả' },
      { value: 'login', text: 'Đăng nhập' },
      { value: 'logout', text: 'Đăng xuất' },
    ];
    const providerOptions = [
      { value: '', text: 'Tất cả' },
      { value: 'username', text: 'Tên tài khoản' },
      { value: 'email', text: 'Email' },
      { value: 'phone', text: 'Số điện thoại' },
      { value: 'facebook', text: 'Facebook' },
      { value: 'google', text: 'Google Mail' },
    ];
    const resolveTypeVariant = (type) => (type === 'login' ? 'success' : 'warning')
    const resolveTypeText = (type) => typeOptions.find((x) => x.value === type).text;
    const resolveProviderText = (provider) => {
      provider = provider || 'username';
      return providerOptions.find((x) => x.value === provider).text;
    }

    const { configDate } = useFileManager();

    const getBrowser = (ua) => {
      if (ua && ua.length) {
        if ((ua.indexOf('OPR')) !== -1) {
          return 'Opera';
        } if (ua.indexOf('Edg') > -1) {
          return 'Edge';
        } if (ua.indexOf('Chrome') !== -1) {
          return 'Chrome';
        } if (ua.indexOf('Safari') !== -1) {
          return 'Safari';
        } if (ua.indexOf('Firefox') !== -1) {
          return 'Firefox';
        } if ((ua.indexOf('MSIE') !== -1) || (!!document.documentMode === true) || !!ua.match(/Trident.*rv\:11\./)) // IF IE > 10
        {
          return 'IE';
        }
      }
      return 'Unknown';
    }
    const deviceType = (ua) => {
      if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        return 'Tablet';
      }
      if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
        return 'Mobile';
      }
      return 'Desktop';
    };

    const fetchUsers = (search, fetchLoading) => {
      if (search.length) {
        fetchLoading(true);
        store.dispatch('users/searchUser', { keyword: search })
          .then((res) => {
            if (res.status === 200) {
              userOptions.value = res.data.data;
            } else {
              userOptions.value = [];
            }
            fetchLoading(false)
          })
      }
    };

    const fetchLog = () => {
      loading.value = true;
      store.dispatch('logs/fetchAuthLog', {
        user_id: searchUserId.value ? searchUserId.value.id : '',
        start_date: startDate.value,
        end_date: endDate.value,
        type: searchType.value,
        provider: searchProvider.value,
        keyword: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        isPaginate: isPaginate.value,
        isOwner: context.root.$can('log.list_all_auth_log', 'Log') ? 0 : 1,
      })
        .then((response) => {
          loading.value = false;
          if (response.status === 200) {
            const items = response.data.data.data;
            const { total } = response.data.data;
            totalEntries.value = total;
            itemsData.value = items;
          }
        });
    };

    const forceLogout = (item) => {
      loading.value = true;
      store.dispatch('logs/authLogForceLogout', { id: item.id })
        .then((res) => {
          loading.value = false;
          if (res.status === 200) {
            item = res.data.data;
            fetchLog();
          }
        })
    };
    const authLogForceLogoutExpired = () => {
      loading.value = true;
      const queryParams = { isOwner: 1 };
      if (context.root.$can('log.list_all_auth_log', 'Log')) {
        queryParams.isOwner = 0;
      }
      store.dispatch('logs/authLogForceLogoutExpired', queryParams)
        .then((res) => {
          loading.value = false;
          if (res.status === 200) {
            fetchLog()
          }
        })
    };
    const confirmForceLogout = (item) => {
      context.root.$swal({
        title: 'Bạn chắc muốn thu hồi?',
        text: 'Thu hồi quyền truy cập trên tài khoản đã đăng nhập có thể ảnh hưởng đến người dùng đang truy cập',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Có',
        cancelButtonText: 'Không',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          forceLogout(item)
        }
      })
    };

    watch([currentPage, perPage, startDate, endDate], () => {
      fetchLog();
    });
    const dataMeta = computed(() => {
      const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
      return {
        from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: totalEntries.value,
      };
    });

    fetchLog();

    return {
      startDate,
      endDate,
      dataMeta,
      loading,
      tableColumns,
      perPage,
      currentPage,
      totalEntries,
      perPageOptions,
      searchUserId,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      itemsData,
      userOptions,
      fetchLog,
      fetchUsers,
      resolveTypeVariant,
      getBrowser,
      deviceType,
      searchType,
      searchProvider,
      typeOptions,
      providerOptions,
      resolveTypeText,
      resolveProviderText,
      forceLogout,
      confirmForceLogout,
      authLogForceLogoutExpired,
      configDate,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
